'use client';

/**
 * "Browse rent-stabilized units in NYC" download-prompt modal.
 *
 * Rendered when a user clicks a rent-stabilized entry point on web
 * (home-page SpecialGems card, listings-page filter chip, etc.) —
 * instead of navigating to the filtered listings page (which is only
 * partially useful on web), we redirect them to install the app where
 * the full rent-stabilized map + calculator lives.
 *
 * Reuses the app's shared responsive-modal pattern (ResponsiveModal =
 * Modal on desktop, BottomSheet on mobile) so the sheet on mobile
 * matches every other bottom sheet in the app.
 *
 * Layout, copy, QR image, and CTAs mirror `CalculatorDownloadSection`
 * on `/nyc-rent-stablized-calculator` so the download experience is
 * consistent across surfaces.
 */

import { ResponsiveModal } from '@/components/ui/ResponsiveModal';
import { Button } from '@/components/ui';
import { Image } from '@/components/ui';
import { IconApple, IconPlayStore } from '@repo/svgs';
import { useAnalytics } from '@/hooks/useAnalytics';

// Same store link used by CalculatorDownloadSection — one Branch link
// that routes to App Store or Play Store based on device. Keep in
// lockstep with that component so future URL changes propagate.
const APP_STORE_URL = 'https://links.openigloo.com/Eqsnv/bbuv/75ka';

// Union of every store-tap event this modal can fire. Kept in
// lockstep with the matching keys in `MiscellaneousEvents` in
// @repo/analytics — this string literal type is what
// `analytics.track()` accepts on its first arg.
type RentStabEvent =
  | 'rent_stabilized_gem_app_store'
  | 'rent_stabilized_gem_play_store'
  | 'rent_stabilized_filter_app_store'
  | 'rent_stabilized_filter_play_store'
  | 'rent_stabilized_apts_app_store'
  | 'rent_stabilized_apts_play_store';

// Three surface families matter for analytics:
//   - `gem_*`    — home-page SpecialGems rent-stab card
//   - `filter_*` — every listings-page filter surface (chip desktop,
//                  chip mobile, "All filters" panel)
//   - `apts_*`   — unit-page "Rent-stabilized apartments" highlight
// Passing a source is still useful because it drops onto the event
// payload for finer-grained analysis if we ever need it.

interface RentStabilizedDownloadModalProps {
  /** Controlled open state. */
  open: boolean;
  /** Called when the modal is dismissed (overlay click, ESC, or
   *  swipe-down on mobile). */
  onClose: () => void;
  /** Analytics `source` — where the modal was triggered from. Only
   *  `'home_special_gems'` maps to the `gem_*` event pair; every
   *  other value (chip, all-filters, etc.) fires the `filter_*` pair.
   *  Value is also passed through on the event payload. */
  source?: string;
  /**
   * Set true when this modal is opened from inside another sheet
   * (e.g. the "All filters" bottom sheet). Bumps the mobile overlay
   * z-index so the parent sheet actually dims behind the child — the
   * same "dark bg layer" effect desktop gets by default.
   */
  nested?: boolean;
}

export function RentStabilizedDownloadModal({
  open,
  onClose,
  source = 'unknown',
  nested = false,
}: RentStabilizedDownloadModalProps) {
  const analytics = useAnalytics();

  // Pick the event pair based on which surface opened the modal.
  //   home_special_gems      → gem_*
  //   unit_page_rent_stab    → apts_*
  //   (everything else)      → filter_*  (chip + all-filters)
  const events: { appStore: RentStabEvent; playStore: RentStabEvent } =
    source === 'home_special_gems'
      ? {
          appStore: 'rent_stabilized_gem_app_store',
          playStore: 'rent_stabilized_gem_play_store',
        }
      : source === 'unit_page_rent_stab'
        ? {
            appStore: 'rent_stabilized_apts_app_store',
            playStore: 'rent_stabilized_apts_play_store',
          }
        : {
            appStore: 'rent_stabilized_filter_app_store',
            playStore: 'rent_stabilized_filter_play_store',
          };

  const trackAndOpen = (event: RentStabEvent) => {
    analytics.track(event, { source });
    window.open(APP_STORE_URL, '_blank', 'noopener,noreferrer');
  };

  return (
    <ResponsiveModal
      open={open}
      onClose={onClose}
      label="Rent-stabilized apartments"
      maxWidth="md"
      nested={nested}
    >
      <section className="flex flex-col gap-4">
        {/* Marketing image (phone mockup). Loaded from `web/public`
            (`/rent-stab-marketing.svg`) rather than S3 to avoid the
            slow initial S3 fetch every time the modal opens — the
            SVG is small enough to bundle with the app. Fixed-height
            wrapper keeps aspect consistent across download surfaces.
            `rounded-2xl` on both wrapper (with overflow-hidden) and
            image so the SVG mask clips cleanly at the corners — same
            pattern used by CalculatorDownloadSection. */}
        <div className="flex flex-row justify-center items-center overflow-hidden w-full h-[228px] md:h-[300px] rounded-2xl md:rounded-3xl">
          <Image
            src="/rent-stab-marketing.svg"
            alt="Openigloo rent-stabilized apartments app screens"
            className="w-full h-full rounded-2xl md:rounded-3xl"
            sizes="(max-width: 768px) 100vw, 600px"
            priority
          />
        </div>

        {/* Header + body copy. Two-line hierarchy matching mobile
            design: bold H2 + one-paragraph body. */}
        <div className="flex flex-col gap-2">
          <h2 className="text-heading-6 md:text-heading-5 text-primary">
            Browse rent-stabilized units in NYC
          </h2>
          <p className="text-body-2 text-primary">
            Get full access in the Openigloo app — browse available rent-stabilized apartments and
            explore buildings with rent-stabilized units in NYC.
          </p>
        </div>

        {/* Buttons + QR row.
            - Mobile (< md): buttons side-by-side, no QR (users are
              already on a phone — QR is meaningless).
            - Desktop (md+): buttons stacked vertically in the left
              column (App Store on top, Play Store below), QR square
              on the right. */}
        <div className="flex md:items-center items-stretch gap-8 pt-2">
          <div className="flex md:flex-col flex-row md:items-stretch items-center gap-4 flex-1">
            <Button
              size="md"
              className="flex-1 md:w-full"
              onClick={() => trackAndOpen(events.appStore)}
              iconLeft={<IconApple className="size-4 shrink-0 icon-inverted" />}
            >
              App Store
            </Button>
            <Button
              size="md"
              variant="secondary"
              className="flex-1 md:w-full"
              onClick={() => trackAndOpen(events.playStore)}
              iconLeft={<IconPlayStore className="size-4 shrink-0 icon-accent" />}
            >
              Play Store
            </Button>
          </div>

          {/* QR — desktop only. Same rendered size + wrapper as the
              calculator page so both surfaces feel identical. */}
          <div className="hidden md:block shrink-0">
            <div className="bg-secondary rounded-2xl p-2 w-fit">
              <Image
                src="/rent-stab-filter-marketing.png"
                alt="Scan to download the Openigloo app"
                className="size-28"
                priority={false}
              />
            </div>
          </div>
        </div>
      </section>
    </ResponsiveModal>
  );
}
